import React, { useState, useEffect } from 'react';
import { Form, FormGroup, Input, Label } from 'reactstrap';
import { Link, useSearchParams } from 'react-router-dom';
import { Btn, H4, P } from '../../../AbstractElements';
import { Password, SignIn } from '../../../Constant';
import { useNavigate } from 'react-router-dom';
import man from '../../../assets/images/login/login_bg.jpg';
import FormPassword from './FormPassword';

const getApiBase = () => {
  const base = process.env.REACT_APP_FW_API;
  if (!base) return '';
  return base.replace(/\/$/, '');
};

/**
 * Одно поле на email и телефон — как в старой админке (avatar\models\forms\Login).
 * Здесь только выбираем, в каком поле отправить значение: API принимает либо
 * email, либо phone (api\models\validate\AuthLogin::ValidateLoginIdentity).
 * Сам номер не нормализуем — на бэкенде это делает UserAvatar::normalizePhoneRu,
 * которая понимает +7…, 8…, и 10 цифр с девятки.
 */
const detectIdentity = (raw) => {
  const value = raw.trim();
  if (!value) return { kind: 'empty' };
  if (value.includes('@')) return { kind: 'email', value };
  if (/^[+\d\s()-]+$/.test(value) && value.replace(/\D+/g, '').length >= 10) {
    return { kind: 'phone', value };
  }
  return { kind: 'unknown', value };
};

const LoginTab = () => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [togglePassword, setTogglePassword] = useState(false);
  const [fieldErrors, setFieldErrors] = useState({ username: '', password: '' });
  const [loading, setLoading] = useState(false);
  const [searchParams] = useSearchParams();
  const requestLkId = searchParams.get('id');

  const setApiError = (usernameMsg, passwordMsg = '') => {
    setFieldErrors({ username: usernameMsg || '', password: passwordMsg || '' });
  };
  const clearApiErrors = () => setFieldErrors({ username: '', password: '' });
  const history = useNavigate();
  const [value, setValue] = useState(localStorage.getItem('profileURL' || man));
  const [name, setName] = useState(localStorage.getItem('Name'));
  useEffect(() => {
    localStorage.setItem('profileURL', man);
  }, [value, name]);

  const firstError = (data, keys) => {
    for (const key of keys) {
      if (Array.isArray(data[key]) && data[key].length) return data[key][0];
    }
    return '';
  };

  const loginAuth = async (e) => {
    e.preventDefault();
    clearApiErrors();

    const identity = detectIdentity(username);
    if (identity.kind === 'empty' || !password) {
      setApiError(
        identity.kind === 'empty' ? 'Введите email или телефон.' : '',
        !password ? 'Введите пароль.' : ''
      );
      return;
    }
    if (identity.kind === 'unknown') {
      setApiError('Введите email (со знаком @) или телефон в формате +7…');
      return;
    }

    const base = getApiBase();
    if (!base) {
      setApiError('Не задан REACT_APP_FW_API.');
      return;
    }

    setLoading(true);
    try {
      const payload = { password };
      if (identity.kind === 'phone') payload.phone = identity.value;
      else payload.email = identity.value;
      if (requestLkId) payload.id = Number(requestLkId);

      const abort = new AbortController();
      const timer = setTimeout(() => abort.abort(), 15000);
      const path = requestLkId ? '/v1/birthday/login-ajax' : '/v1/auth/login';

      let res;
      try {
        res = await fetch(`${base}${path}`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload),
          signal: abort.signal,
        });
      } finally {
        clearTimeout(timer);
      }
      const data = await res.json().catch(() => ({}));

      const token = data.access_token_180 || data.access_token;
      if (res.ok && token) {
        setValue(man);
        setName(username.trim());
        localStorage.setItem('token', token);
        localStorage.setItem('login', JSON.stringify(true));
        history(`${process.env.PUBLIC_URL}${requestLkId ? '/grant' : '/home'}`);
        return;
      }
      if (res.status === 401) {
        setApiError(data.message || data.error || 'Неверный логин или пароль.');
        return;
      }
      if (res.status === 400 && data && typeof data === 'object') {
        const identityMsg = firstError(data, ['email', 'phone', 'id']);
        const passwordMsg = firstError(data, ['password']);
        const fallback = data.message || data.error || 'Ошибка валидации.';
        setApiError(
          identityMsg || (passwordMsg ? '' : fallback),
          passwordMsg || (identityMsg ? '' : fallback)
        );
        return;
      }
      setApiError(data.message || data.error || 'Ошибка входа. Попробуйте позже.');
    } catch (err) {
      setApiError(
        err && err.name === 'AbortError'
          ? 'Сервер не ответил. Проверьте связь и попробуйте ещё раз.'
          : 'Не удалось связаться с сервером. Попробуйте ещё раз через минуту.'
      );
    } finally {
      setLoading(false);
    }
  };

  const hasUsernameError = Boolean(fieldErrors.username);
  const hasPasswordError = Boolean(fieldErrors.password);
  const base = process.env.PUBLIC_URL || '';
  const tabQuery = requestLkId ? `?id=${encodeURIComponent(requestLkId)}` : '';

  return (
      <Form className='theme-form needs-validation' noValidate onSubmit={(e) => { e.preventDefault(); loginAuth(e); }}>
        <H4>Вход в FairyWorld</H4>
        <P>{'Убедитесь, что вы находитесь на сайте '}{window.location.host}</P>
        <FormGroup>
          <Label className='col-form-label'>Email или телефон</Label>
          <Input
            className={hasUsernameError ? 'form-control is-invalid' : 'form-control'}
            type='text'
            inputMode='email'
            autoComplete='username'
            placeholder='you@example.com или +7…'
            onChange={(e) => { setUsername(e.target.value); clearApiErrors(); }}
            value={username}
          />
          {hasUsernameError && <div className='invalid-feedback d-block'>{fieldErrors.username}</div>}
        </FormGroup>
        <FormGroup className='position-relative'>
          <Label className='col-form-label'>{Password}</Label>
          <div className='position-relative'>
            <Input
              className={hasPasswordError ? 'form-control is-invalid' : 'form-control'}
              type={togglePassword ? 'text' : 'password'}
              autoComplete='current-password'
              onChange={(e) => { setPassword(e.target.value); clearApiErrors(); }}
              value={password}
            />
            <div className='show-hide' onClick={() => setTogglePassword(!togglePassword)}><span className={togglePassword ? '' : 'show'}></span></div>
          </div>
          {hasPasswordError && <div className='invalid-feedback d-block'>{fieldErrors.password}</div>}
        </FormGroup>
        <FormPassword/>
        <div>
          <Btn attrBtn={{ color: 'primary', className: 'd-block w-100 mt-2', onClick: (e) => loginAuth(e), disabled: loading }}>{loading ? 'Вход...' : SignIn}</Btn>
        </div>
        <P attrPara={{ className: 'mt-3 mb-0 text-center' }}>
          {'Нет аккаунта? '}<Link to={`${base}/register${tabQuery}`}>Регистрация</Link>
        </P>
      </Form>
  );
};

export default LoginTab;
